import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ShopSettings, ShopSettingsRepository } from '@infrab4a/connect';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { FileControllerService } from 'src/app/admin-api';
import { CustomizationFormComponent } from 'src/app/components/customization/customization-form.component';
import { ImageService } from 'src/app/services/image.service';

@Component({
  selector: 'app-glam-beauty-banner-form',
  templateUrl: './glam-beauty-banner-form.component.html',
  styleUrls: ['./glam-beauty-banner-form.component.scss']
})
export class GlamBeautyBannerFormComponent
  extends CustomizationFormComponent
  implements OnInit
{
  pageDocument: ShopSettings;
  readonly mobileImageWidthPx = 690;
  readonly mobileImageHeightPx = 350;
  readonly desktopImageWidthPx = 2432;
  readonly desktopImageHeightPx = 500;
  readonly maxImageFileSizeInBytes = 250 * 1024;

  constructor(
    @Inject('ShopSettingsRepository')
    private shopSettingsRepository: ShopSettingsRepository,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private activatedRoute: ActivatedRoute,
    override router: Router,
    override imageService: ImageService,
    override config: PrimeNGConfig,
    override fileService: FileControllerService
  ) {
    super(router, config, imageService, fileService);

    this.documentId = 'GLAM_BEAUTY';

    this.form = this.formBuilder.group({
      desktopImage: ['', [Validators.required]],
      mobileImage: ['', [Validators.required]],
      title: ['', [Validators.required]],
      altText: [
        '',
        [
          Validators.required,
          Validators.minLength(50),
          Validators.maxLength(125)
        ]
      ],
      link: [''],
      active: [true]
    });
  }

  async ngOnInit() {
    await this.getDocument();
  }

  override async getDocument(): Promise<void> {
    try {
      this.pageDocument = await this.shopSettingsRepository.get({
        id: this.documentId
      });
      this.documentSections = this.pageDocument.sections || [];
      if (this.router.url.includes('edit')) {
        this.activatedRoute.params.subscribe((params) => {
          this.activeIndex = +params['index'];
          this.activeSection = this.documentSections[this.activeIndex];
          this.initializeForm();
        });
      }
    } catch (error) {
      this.form.patchValue(this.activeSection);
    }
  }

  override initializeForm(): void {
    this.form.patchValue(this.activeSection);
  }

  override async save() {
    if (this.form.valid) {
      const doc = this.form.value;

      if (this.activeSection) this.documentSections[this.activeIndex] = doc;
      else this.documentSections.push(doc);

      await this.shopSettingsRepository.update({
        id: this.documentId,
        sections: this.documentSections
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Salvo com sucesso',
        detail: 'As informações foram salvas.'
      });
      await this.afterSubmit();
      this.navigate('/configuracoes/glam-beauty/carousels');
    } else {
      console.error('Formulário inválido. Não é possível salvar os dados.');
    }
  }

  isAltTextInvalid() {
    const altTextControl = this.form.get('altText');
    return (
      altTextControl?.invalid &&
      (altTextControl?.touched || altTextControl?.dirty)
    );
  }

  isTitleInvalid() {
    const titleControl = this.form.get('title');
    return (
      titleControl?.invalid && (titleControl?.touched || titleControl?.dirty)
    );
  }

  async handleFileSelect(
    event: { files: File[] },
    inputName: string,
    expectedWidth: number,
    expectedHeight: number,
    maxWeight: number,
    pInput: FileUpload
  ) {
    this.fileNamePattern = `glam-beauty-banner-${new Date().getTime()}.webp`;

    await this.upload(
      event,
      inputName,
      expectedWidth,
      expectedHeight,
      maxWeight,
      pInput
    );
  }

  goToMenu() {
    this.navigate('configuracoes/glam-beauty/carousels');
  }

  get desktopBannerUrl(): string {
    return this.form.get('desktopImage')?.value;
  }

  get mobileBannerUrl(): string {
    return this.form.get('mobileImage')?.value;
  }

  override get filePath(): string {
    return '/pages/glam-beauty/';
  }

  override get model() {
    return this.pageDocument;
  }

  override get modelId() {
    return this.pageDocument?.id;
  }
}
